import React, {useState, useEffect} from 'react'
import moment from "moment"
import _ from 'lodash'
import { Button, Icon, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { DayPickerRangeController } from 'react-dates'
import "react-dates/initialize"
import isInclusivelyAfterDay from 'react-dates/lib/utils/isInclusivelyAfterDay'
import TimeKeeper from 'react-timekeeper'
import { useAlert } from 'src/common/razzle-platform-components/alert'
import axios from 'src/common/razzle-platform-components/axios'
import { handleErrors } from 'src/common/razzle-platform-components/utils'
import { useMobileDetector } from 'src/common/razzle-platform-components/hooks/mobile-detector'
import { useIntl } from 'src/common/razzle-platform-translate'
import {
    AVAILABILITY_DATE_STRING_FORMAT,
    AVAILABILITY_FILTER_USE_TIME,
    BASE_API_URL,
    OPTIONS_OF_PRODUCT_API_END_POINT
} from '../configs'


const defaultProps = {
    // example props for the demo
    autoFocusEndDate: false,
    startDateOffset: undefined,
    endDateOffset: undefined,
    showInputs: false,
    minDate: moment(),
    maxDate: null,

    // day presentation and interaction related props
    renderCalendarDay: undefined,
    renderDayContents: null,
    minimumNights: 1,
    isDayBlocked: () => false,
    isOutsideRange: day => !isInclusivelyAfterDay(day, moment()),
    isDayHighlighted: () => false,
    enableOutsideDays: false,
    daysViolatingMinNightsCanBeClicked: false,

    // calendar presentation and interaction related props
    orientation: "horizontal",
    verticalHeight: undefined,
    withPortal: false,
    initialVisibleMonth: null,
    numberOfMonths: 2,
    onOutsideClick() {},
    keepOpenOnDateSelect: false,
    renderCalendarInfo: null,
    isRTL: false,
    renderMonthText: null,
    renderMonthElement: null,
    renderKeyboardShortcutsButton: undefined,
    renderKeyboardShortcutsPanel: undefined,

    // navigation related props
    navPrev: null,
    navNext: null,
    renderNavPrevButton: null,
    renderNavNextButton: null,
    onPrevMonthClick() {},
    onNextMonthClick() {},

    // internationalization
    monthFormat: 'MMMM YYYY',
};

const START_DATE = 'startDate';
const END_DATE = 'endDate';

const useStyles = makeStyles((theme) => ({
    displayTimeContainer:  {
        fontSize: '1.2rem',
        textTransform: 'uppercase',
        display: 'block',
        cursor: 'pointer',
        color: '#b5b5b5',
    },
    displayTime: {
        textAlign: 'center',
        display: 'block',
        fontSize: '1.8rem',
        marginTop: '4px',
        transition: '.15s ease',
        fontWeight: 'bolder'
    },
    arrowforwardBetweenTimes: {
        lineHeight: '32px',
        marginLeft: 25,
        marginRight: 25
    },
    timePickerWrapper: {
        position: 'fixed',
        marginTop: '-341px',
        zIndex: 99999,
        marginLeft: -70
    }
}))


function AvailabilityFilter(props) {

    const classes = useStyles();
    const {
        onClear,
        onSave,
        startDate,
        endDate,
        onChange,
        extraAvailabilityFilters: ExtraAvailabilityFilters,
        filters,
        clearFilter
    } = props
    const { isMobile } = useMobileDetector()
    const { formatMessage } = useIntl()
    const [focused, setFocused] = useState(defaultProps.autoFocusEndDate ? END_DATE : START_DATE)
    const {errorAlert} = useAlert()
    const [showStartTime, setShowStartTime] = useState(false)
    const [showEndTime, setShowEndTime] = useState(false)
    const [periodicities, setPeriodicities] = useState([])


    useEffect(() => {
        // we import styles in this platce for avoid the problem when build the app
        require("react-dates/lib/css/_datepicker.css")
        require("./AvailabilityFilter.css")

        if (_.isEmpty(periodicities)) {
			fetchPeriodicities()
        }
    }, [])

    const fetchPeriodicities = async () => {
		try {
			const params = {
				uuid: process.env.NEXT_PUBLIC_RESERVATION_OPTION_UUID,
				_embed:'periodicities'
			}
			const response = await axios.get(`${BASE_API_URL}${OPTIONS_OF_PRODUCT_API_END_POINT}`, {params});
			setPeriodicities(response.data[0].periodicities)
		} catch (error) {
            errorAlert(handleErrors(error))
		}
    }

    const dayPickerRangeProps = _.omit(defaultProps, [
        'autoFocus',
        'autoFocusEndDate',
        'initialStartDate',
        'initialEndDate',
        'showInputs',
    ]);

    const onDatesChange = ({ startDate: start, endDate: end }) => {
        const { daysViolatingMinNightsCanBeClicked, minimumNights } = props;
        let doesNotMeetMinNights = false;
        if (daysViolatingMinNightsCanBeClicked && start && end) {
          const dayDiff = end.diff(start.clone().startOf('day').hour(12), 'days');
          doesNotMeetMinNights = dayDiff < minimumNights && dayDiff >= 0;
        }

        let returnedStartDate = start
        let returnedEndDate = end

        if (start && end && end.isBefore(start)) {
            returnedEndDate = null
        }

        onChange({
            startDate: returnedStartDate?.format(AVAILABILITY_DATE_STRING_FORMAT),
            endDate: doesNotMeetMinNights ? null : returnedEndDate?.format(AVAILABILITY_DATE_STRING_FORMAT)
        })
    }

    const handleStartTimeChange = (newTime) => {
        if (startDate) {
            const value = startDate.clone().set({minutes: newTime.minute, hours: newTime.hour})
            onChange({startDate: value.format(AVAILABILITY_DATE_STRING_FORMAT)})
        }
    }

    const handleEndTimeChange = (newTime) => {
        if (endDate) {
            const value = endDate.clone().set({minutes: newTime.minute, hours: newTime.hour})
            onChange({endDate: value.format(AVAILABILITY_DATE_STRING_FORMAT)})
        }
    }

    return (
        <Paper className="p-8 pb-0 pr-0 pl-0 pt-0">
            <div className="flex flex-col">

                <div className="flex flex-col">
                    <div>
                    <DayPickerRangeController
                        {...dayPickerRangeProps}
                        onDatesChange={onDatesChange}
                        onFocusChange={focusedInput => setFocused(!focusedInput ? START_DATE : focusedInput)}
                        focusedInput={focused}
                        startDate={startDate}
                        endDate={endDate}
                        hideKeyboardShortcutsPanel={true}
                        noBorder
                        numberOfMonths={isMobile ? 1 : 2}

                    />
                    </div>
                    { AVAILABILITY_FILTER_USE_TIME &&
                        <div className="flex items-center justify-center mt-0 mr-4 ml-4 mb-0">
                            <div className="intro-demo-wrapper">
                                <div className="intro-demo-picker">
                                    {showStartTime && (
                                        <div className={classes.timePickerWrapper}>
                                            <TimeKeeper
                                                time={startDate ? startDate.format('LT') : '12:00 pm'}
                                                onChange={handleStartTimeChange}
                                                switchToMinuteOnHourSelect
                                                closeOnMinuteSelect
                                                onDoneClick={() => setShowStartTime(false)}
                                                doneButton={() => <div />}
                                            />
                                        </div>
                                    )}
                                </div>
                                <span onClick={() => !showEndTime && setShowStartTime(true)} className={classes.displayTimeContainer}>
                                    <Typography color="primary" className={classes.displayTime} >{startDate?.format('LT') || '12:00 pm'}</Typography>

                                </span>
                            </div>
                            <div>
                                <Icon className={classes.arrowforwardBetweenTimes} >arrow_forward</Icon>
                            </div>
                            <div className="intro-demo-wrapper">
                                <div className="intro-demo-picker">
                                    {showEndTime && (
                                        <div className={classes.timePickerWrapper}>
                                            <TimeKeeper
                                                time={endDate ? endDate.format('LT') : '12:00 pm'}
                                                onChange={handleEndTimeChange}
                                                switchToMinuteOnHourSelect
                                                closeOnMinuteSelect
                                                onDoneClick={() => setShowEndTime(false)}
                                                doneButton={() => <div />}
                                            />
                                        </div>
                                    )}
                                </div>
                                <span onClick={() => !showStartTime && setShowEndTime(true)} className={classes.displayTimeContainer}>
                                    <Typography color="primary" className={classes.displayTime} >{endDate?.format('LT') || '12:00 pm'}</Typography>
                                </span>
                            </div>
                        </div>
                    }
                    { ExtraAvailabilityFilters &&
                        <div className="flex mt-0 mr-4 ml-4 mb-0">
                            <ExtraAvailabilityFilters
                                isMobile={isMobile}
                                onChange={onChange}
                                startDate={startDate}
                                endDate={endDate}
                                filters={filters}
                                clearFilter={clearFilter}
                                periodicities={periodicities}
                            />
                        </div>
                    }
                    {!isMobile &&
                        <div className="flex items-center justify-between mt-0 mr-4 ml-4 mb-4">
                            <div className="flex items-center">
                                { (startDate && endDate) && <Button onClick={onClear} variant="text" className="text-gray-600">
                                    <Typography>{formatMessage({ id: 'Clear', defaultMessage: 'Clear' })}</Typography>
                                </Button>}
                            </div>
                            <div className="flex items-center justify-end">
                                <Button onClick={onSave} variant="text">
                                    <Typography>{formatMessage({ id: 'Save', defaultMessage: 'Save' })}</Typography>
                                </Button>
                            </div>
                        </div>
                    }

                </div>
            </div>
        </Paper>
    )
}

export default AvailabilityFilter


